import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import BackupIcon from "@mui/icons-material/Backup";
import RefreshIcon from "@mui/icons-material/Refresh";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import { useState } from "react";
import { toast } from "react-toastify";
import { listBackups, triggerBackup } from "../../../services/api/admin/adminBackupApi";

export default function BackupManagement() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const fetchBackups = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listBackups();
      setBackups(data.backups || []);
      setLoaded(true);
    } catch (err) {
      setError("Erro ao carregar backups");
    } finally {
      setLoading(false);
    }
  };

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      await triggerBackup();
      toast.success("Backup iniciado! Aguarde alguns minutos e atualize a lista.");
    } catch (err) {
      toast.error("Erro ao iniciar backup");
    } finally {
      setTriggering(false);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return "-";
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
  };

  const formatDate = (name) => {
    // Extrair data do nome do arquivo: backup_YYYY-MM-DD_HH-MM-SS.sql.gz
    const match = name?.match(/(\d{4}-\d{2}-\d{2})_(\d{2}-\d{2}-\d{2})/);
    if (!match) return name;
    const [, date, time] = match;
    return `${date} ${time.replace(/-/g, ":")}`;
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h6">
          <CloudDoneIcon sx={{ mr: 1, verticalAlign: "middle" }} />
          Backups MySQL (OneDrive)
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchBackups}
            disabled={loading}
          >
            {loaded ? "Atualizar" : "Carregar"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={triggering ? <CircularProgress size={18} color="inherit" /> : <BackupIcon />}
            onClick={handleTrigger}
            disabled={triggering}
          >
            {triggering ? "Iniciando..." : "Fazer Backup Agora"}
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Backup automático: toda sexta-feira às 03:00 (horário de Brasília). Os backups são enviados para o OneDrive.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : !loaded ? (
        <Alert severity="info">Clique em "Carregar" para ver os backups disponíveis.</Alert>
      ) : backups.length === 0 ? (
        <Alert severity="warning">Nenhum backup encontrado no OneDrive.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Arquivo</strong></TableCell>
                <TableCell><strong>Data</strong></TableCell>
                <TableCell align="right"><strong>Tamanho</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {backups.map((backup, idx) => (
                <TableRow key={backup.id || idx}>
                  <TableCell>{backup.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={formatDate(backup.name)}
                      size="small"
                      color={idx === 0 ? "success" : "default"}
                      variant={idx === 0 ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell align="right">{formatSize(backup.size)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
